import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@acto/web-common/auth";
import { defaultNS, LocalePreferenceContext } from "@acto/web-common/locales";
import { useUserLocale } from "@acto/taskpane/core/data-access/user/useUserLocale";

export function UserLocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const isTranslated = useCallback(
    (locale: string | null | undefined): locale is string =>
      locale != null && i18n.hasResourceBundle(locale, defaultNS),
    [i18n],
  );
  const { locale, fetching, saveLocale } = useUserLocale();
  const active = useRef(true);
  const selected = useRef(false);
  const saving = useRef(false);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    // Never upload a browser-local value on login: it may belong to a different user.
    // A late read must not overwrite a selection already made in this session.
    if (!fetching && isTranslated(locale) && !selected.current) {
      void i18n.changeLanguage(locale);
    }
  }, [locale, fetching, i18n, isTranslated]);

  const setLocale = useCallback(
    async (next: string) => {
      if (!isTranslated(next) || saving.current) throw new Error("Language change unavailable");
      saving.current = true;
      try {
        await saveLocale(next);
        selected.current = true;
        if (active.current) await i18n.changeLanguage(next);
      } finally {
        saving.current = false;
      }
    },
    [saveLocale, i18n, isTranslated],
  );
  const value = useMemo(() => ({ setLocale }), [setLocale]);

  return <LocalePreferenceContext.Provider value={value}>{children}</LocalePreferenceContext.Provider>;
}

/** Remount preferences when the authenticated user or tenant changes. */
export function AuthenticatedLocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  if (!user) return <>{children}</>;
  return <UserLocaleProvider key={`${user.tenantId}:${user.id}`}>{children}</UserLocaleProvider>;
}
